import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  BadgeInfo,
  ChevronDown,
  ChevronUp,
  GalleryHorizontal,
  Loader2,
  Map,
  MousePointer2,
  Move3D,
  Sparkles,
} from 'lucide-react';
import { loadGalleryArtworks } from './data';
import { authenticateGalleryEditor, loadGalleryLayout, saveGalleryLayout } from './layoutApi';
import { GalleryScene } from './playCanvasGalleryScene';
import type { ArtworkPlacement, GalleryArtwork, GallerySceneSelection, GalleryStats } from './types';

const frameLabels = {
  shelf: '架子画 / Object Shelf',
  wall: '壁画 / Wall Work',
  mural: '横幅壁画 / Mural',
  monumental: '单体巨幅 / Monumental',
};

const builderStorageKey = 'artchais-gallery-world-builder-draft-v2';
const editorTokenStorageKey = 'artchais-gallery-editor-token';
const urlParams = new URLSearchParams(window.location.search);
const sceneParam = urlParams.get('scene');
const sceneVariant = sceneParam === 'rdfz' ? 'rdfz' : 'secret';
const showRdfzArtworks = urlParams.get('artworks') === '1';
const visualArtworksEnabled = sceneVariant !== 'rdfz' || showRdfzArtworks;

const minimapBounds = {
  rdfz: { minX: -13, maxX: 13, minZ: -18, maxZ: 18 },
  secret: { minX: -32, maxX: 32, minZ: -22.5, maxZ: 22.5 },
};

const minimapWalls = {
  rdfz: [
    { id: 'rdfz-back', x: 0, z: -16.2, width: 22, depth: 0.6 },
    { id: 'rdfz-front', x: 0, z: 16.2, width: 22, depth: 0.6 },
    { id: 'rdfz-left', x: -11.5, z: 0, width: 0.6, depth: 31 },
    { id: 'rdfz-right', x: 11.5, z: 0, width: 0.6, depth: 31 },
    { id: 'rdfz-mid-a', x: 0, z: -3.8, width: 18, depth: 0.45 },
    { id: 'rdfz-mid-b', x: 0, z: 7.8, width: 18, depth: 0.45 },
  ],
  secret: [
    { id: 'north', x: 0, z: -21.6, width: 62, depth: 0.72 },
    { id: 'west', x: -31.2, z: -0.4, width: 0.72, depth: 43 },
    { id: 'east', x: 31.2, z: -0.4, width: 0.72, depth: 43 },
    { id: 'south-left', x: -20.5, z: 21.6, width: 21, depth: 0.68 },
    { id: 'south-right', x: 20.5, z: 21.6, width: 21, depth: 0.68 },
    { id: 'left-long', x: -11.4, z: -4.1, width: 0.68, depth: 25 },
    { id: 'right-long', x: 11.4, z: -4.1, width: 0.68, depth: 25 },
    { id: 'left-front', x: -10.2, z: 9.2, width: 13.2, depth: 0.64 },
    { id: 'right-front', x: 10.2, z: 9.2, width: 13.2, depth: 0.64 },
    { id: 'left-diagonal', x: -22.2, z: 5.2, width: 12.4, depth: 0.64, rotationY: -0.44 },
    { id: 'right-diagonal', x: 22.2, z: 5.2, width: 12.4, depth: 0.64, rotationY: 0.44 },
    { id: 'rear-cross', x: 0, z: -10.8, width: 18, depth: 0.7 },
  ],
};

const minimapCircles = {
  rdfz: [],
  secret: [
    { id: 'pool', x: 0, z: -2.2, radius: 8.0, kind: 'pool' },
    { id: 'column-nw', x: -26, z: -17.6, radius: 0.52, kind: 'column' },
    { id: 'column-ne', x: 26, z: -17.6, radius: 0.52, kind: 'column' },
    { id: 'column-sw', x: -26, z: 15.8, radius: 0.52, kind: 'column' },
    { id: 'column-se', x: 26, z: 15.8, radius: 0.52, kind: 'column' },
    { id: 'column-mid-l', x: -6.8, z: -2.2, radius: 0.52, kind: 'column' },
    { id: 'column-mid-r', x: 6.8, z: -2.2, radius: 0.52, kind: 'column' },
    { id: 'column-front-l', x: -15.8, z: 10.8, radius: 0.52, kind: 'column' },
    { id: 'column-front-r', x: 15.8, z: 10.8, radius: 0.52, kind: 'column' },
  ],
};

function clampPercent(value: number) {
  return Math.min(94, Math.max(6, value));
}

function worldToMinimapPoint(x: number, z: number, variant: typeof sceneVariant) {
  const bounds = minimapBounds[variant];
  return {
    x: clampPercent(((x - bounds.minX) / (bounds.maxX - bounds.minX)) * 100),
    y: clampPercent(((z - bounds.minZ) / (bounds.maxZ - bounds.minZ)) * 100),
  };
}

function worldRectToMinimapStyle(
  rect: { x: number; z: number; width: number; depth: number; rotationY?: number },
  variant: typeof sceneVariant,
) {
  const bounds = minimapBounds[variant];
  const point = worldToMinimapPoint(rect.x, rect.z, variant);
  return {
    left: `${point.x}%`,
    top: `${point.y}%`,
    width: `${(rect.width / (bounds.maxX - bounds.minX)) * 100}%`,
    height: `${(rect.depth / (bounds.maxZ - bounds.minZ)) * 100}%`,
    transform: `translate(-50%, -50%) rotate(${-(rect.rotationY || 0)}rad)`,
  };
}

function worldCircleToMinimapStyle(
  circle: { x: number; z: number; radius: number },
  variant: typeof sceneVariant,
) {
  const bounds = minimapBounds[variant];
  const point = worldToMinimapPoint(circle.x, circle.z, variant);
  return {
    left: `${point.x}%`,
    top: `${point.y}%`,
    width: `${((circle.radius * 2) / (bounds.maxX - bounds.minX)) * 100}%`,
    height: `${((circle.radius * 2) / (bounds.maxZ - bounds.minZ)) * 100}%`,
  };
}

function formatPrice(selection: GallerySceneSelection | null) {
  if (!selection?.price) return 'Open archive';
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: selection.currency || 'CNY',
    maximumFractionDigits: 0,
  }).format(selection.price);
}

function App() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<GalleryScene | null>(null);
  const editorEnabled = useMemo(() => {
    return new URLSearchParams(window.location.search).has('editor') || window.location.pathname.startsWith('/builder');
  }, []);
  const [artworks, setArtworks] = useState<GalleryArtwork[]>([]);
  const [stats, setStats] = useState<GalleryStats | null>(null);
  const [selection, setSelection] = useState<GallerySceneSelection | null>(null);
  const [editorLayout, setEditorLayout] = useState<ArtworkPlacement[]>([]);
  const [selectedPlacementId, setSelectedPlacementId] = useState('');
  const [selectedArtworkId, setSelectedArtworkId] = useState('');
  const [editorAuthenticated, setEditorAuthenticated] = useState(() => {
    return !editorEnabled || Boolean(window.sessionStorage.getItem(editorTokenStorageKey));
  });
  const [editorPassword, setEditorPassword] = useState('');
  const [editorAuthError, setEditorAuthError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [visitor, setVisitor] = useState({ x: 0, z: 8.8, yaw: 0 });
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const builderActive = editorEnabled && editorAuthenticated;
  const handleSceneSelect = useCallback((next: GallerySceneSelection | null) => {
    setSelection((current) => {
      if (!current && !next) return current;
      if (current?.id === next?.id && current?.imageUrl === next?.imageUrl) return current;
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadGalleryArtworks()
      .then((result) => {
        if (cancelled) return;
        setArtworks(result.artworks);
        setStats(result.stats);
        setLoadError('');
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error instanceof Error ? error.message : 'Gallery data failed to load.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    sceneRef.current?.dispose();
    sceneRef.current = new GalleryScene({
      canvas: canvasRef.current,
      builderMode: editorEnabled,
      previewEnabled: builderActive,
      sceneVariant,
      displayArtworks: visualArtworksEnabled,
      onSelect: handleSceneSelect,
      onPlace: (placement) => {
        const nextLayout = sceneRef.current?.getArtworkPlacements() || [];
        setEditorLayout(nextLayout);
        setSelectedPlacementId(placement.id);
      },
      onMove: setVisitor,
    });
    return () => {
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, [builderActive, handleSceneSelect]);

  useEffect(() => {
    if (artworks.length === 0) return;
    let cancelled = false;
    loadGalleryLayout().then((publishedLayout) => {
      if (cancelled) return;
      const stored = window.localStorage.getItem(builderStorageKey);
      const localDraft = stored ? (JSON.parse(stored) as ArtworkPlacement[]) : [];
      const savedLayout = visualArtworksEnabled
        ? publishedLayout.length > 0 ? publishedLayout : builderActive ? localDraft : []
        : [];
      sceneRef.current?.setArtworks(artworks, savedLayout, visualArtworksEnabled && !editorEnabled && savedLayout.length === 0);
      const nextLayout = sceneRef.current?.getArtworkPlacements() || [];
      setEditorLayout(nextLayout);
      setSelectedArtworkId((current) => current || artworks[0]?.id || '');
      setSelectedPlacementId((current) => current || nextLayout[0]?.id || '');
    });
    return () => {
      cancelled = true;
    };
  }, [artworks, builderActive]);

  const frameCounts = useMemo(() => {
    return artworks.reduce(
      (acc, artwork) => {
        acc[artwork.frameType] += 1;
        return acc;
      },
      { shelf: 0, wall: 0, mural: 0, monumental: 0 },
    );
  }, [artworks]);

  const mapPoints = useMemo(() => {
    const selectedId = selection?.id;
    return editorLayout.map((placement) => {
      const point = worldToMinimapPoint(placement.x, placement.z, sceneVariant);
      return {
        id: placement.id,
        x: point.x,
        y: point.y,
        active: placement.artworkId === selectedId,
      };
    });
  }, [editorLayout, selection?.id]);

  const visitorPoint = useMemo(() => {
    return worldToMinimapPoint(visitor.x, visitor.z, sceneVariant);
  }, [visitor.x, visitor.z]);

  const selectedPlacement = editorLayout.find((item) => item.id === selectedPlacementId) || editorLayout[0];
  const selectedArtwork = artworks.find((item) => item.id === selectedArtworkId);

  function updateSelectedPlacement(patch: Partial<ArtworkPlacement>) {
    if (!selectedPlacement) return;
    const next = { ...selectedPlacement, ...patch };
    sceneRef.current?.updateArtworkPlacement(selectedPlacement.id, next);
    setEditorLayout((items) => items.map((item) => (item.id === selectedPlacement.id ? next : item)));
  }

  async function unlockEditor(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEditorAuthError('');
    try {
      const token = await authenticateGalleryEditor(editorPassword);
      window.sessionStorage.setItem(editorTokenStorageKey, token);
      setEditorAuthenticated(true);
      setEditorPassword('');
    } catch (error) {
      setEditorAuthError(error instanceof Error ? error.message : 'Password check failed.');
    }
  }

  async function saveLayout() {
    window.localStorage.setItem(builderStorageKey, JSON.stringify(editorLayout));
    const token = window.sessionStorage.getItem(editorTokenStorageKey);
    if (!token) {
      setSaveStatus('Saved locally. Unlock editor to publish.');
      return;
    }
    setSaveStatus('Saving...');
    try {
      await saveGalleryLayout(editorLayout, token);
      setSaveStatus('Saved to gallery.');
    } catch (error) {
      setSaveStatus(error instanceof Error ? error.message : 'Remote save failed. Local draft kept.');
    }
  }

  function placeSelectedArtwork() {
    const placement = sceneRef.current?.addFrameFromArtwork(selectedArtworkId);
    if (!placement) return;
    const nextLayout = sceneRef.current?.getArtworkPlacements() || [];
    setEditorLayout(nextLayout);
    setSelectedPlacementId(placement.id);
  }

  function selectArtworkForPreview(artworkId: string) {
    setSelectedArtworkId(artworkId);
    sceneRef.current?.setPreviewArtwork(artworkId);
  }

  function removeSelectedPlacement() {
    if (!selectedPlacement) return;
    sceneRef.current?.removeArtworkPlacement(selectedPlacement.id);
    const nextLayout = sceneRef.current?.getArtworkPlacements() || [];
    setEditorLayout(nextLayout);
    setSelectedPlacementId(nextLayout[0]?.id || '');
  }

  return (
    <main className={editorEnabled ? 'gallery-shell editor-shell' : 'gallery-shell'}>
      <canvas ref={canvasRef} className="gallery-canvas" aria-label="ArtChais 3D gallery viewport" />

      {loading && (
        <div className="loading-panel">
          <Loader2 className="spin" size={24} />
          <span>Preparing gallery lighting</span>
        </div>
      )}

      {loadError && <div className="error-panel">{loadError}</div>}

      <section className="top-left panel">
        <div className="brand-row">
          <span className="brand-mark">
            <GalleryHorizontal size={20} />
          </span>
          <div>
            <strong>ArtChais Gallery</strong>
            <small>gallery.artchais.com</small>
          </div>
        </div>
        <div className="metric-row">
          <span>{visualArtworksEnabled ? `${artworks.length} works installed` : 'Venue model only'}</span>
          <span>
            {!visualArtworksEnabled
              ? 'Artwork layer hidden'
              : stats?.source === 'manifest'
              ? 'Bucket manifest'
              : stats?.source === 'fallback'
                ? 'Fallback set'
                : 'Live marketplace'}
          </span>
        </div>
        {visualArtworksEnabled ? (
          <div className="frame-grid">
            <span>架子画 {frameCounts.shelf}</span>
            <span>壁画 {frameCounts.wall}</span>
            <span>横幅 {frameCounts.mural}</span>
            <span>巨幅 {frameCounts.monumental}</span>
          </div>
        ) : (
          <div className="frame-grid venue-grid">
            <span>白墙</span>
            <span>绿台</span>
            <span>玻璃</span>
            <span>木柱</span>
          </div>
        )}
      </section>

      <section className="top-center panel exhibition-title">
        <small>VIRTUAL EXHIBITION</small>
        <h1>{sceneVariant === 'rdfz' ? 'RDFZ Graduation Gallery' : 'Grand Atrium Gallery'}</h1>
        <p>
          {sceneVariant === 'rdfz'
            ? visualArtworksEnabled
              ? 'Designer venue · cleaned FBX scene · ArtChais installation layer'
              : 'Designer venue study · artwork layer hidden'
            : 'Secret-room venue · Blender prototype · directional lighting plan'}
        </p>
      </section>

      <section className={mapCollapsed ? 'top-right panel minimap-panel collapsed' : 'top-right panel minimap-panel'}>
        <div className="panel-title">
          <Map size={16} />
          <span>Lighting Map</span>
          <button
            type="button"
            className="icon-toggle"
            onClick={() => setMapCollapsed((value) => !value)}
            aria-label={mapCollapsed ? 'Expand lighting map' : 'Collapse lighting map'}
          >
            {mapCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
        {!mapCollapsed && (
          <div className="minimap">
            {minimapWalls[sceneVariant].map((wall) => (
              <span
                key={wall.id}
                className="map-wall"
                style={worldRectToMinimapStyle(wall, sceneVariant)}
              />
            ))}
            {minimapCircles[sceneVariant].map((circle) => (
              <span
                key={circle.id}
                className={`map-circle ${circle.kind}`}
                style={worldCircleToMinimapStyle(circle, sceneVariant)}
              />
            ))}
            <span
              className="visitor-dot"
              style={{
                left: `${visitorPoint.x}%`,
                top: `${visitorPoint.y}%`,
                transform: `translate(-50%, -50%) rotate(${visitor.yaw}rad)`,
              }}
            />
            {visualArtworksEnabled && mapPoints.map((point) => (
              <span
                key={point.id}
                className={point.active ? 'art-dot active' : 'art-dot'}
                style={{ left: `${point.x}%`, top: `${point.y}%` }}
              />
            ))}
          </div>
        )}
      </section>

      {selection && (
        <section className="right-info panel">
          <div className="panel-title">
            <BadgeInfo size={17} />
            <span>Artwork Info</span>
          </div>
            <img src={selection.imageUrl} alt={selection.title} className="selected-image" />
            <h2>{selection.title}</h2>
            <p className="artist-line">by {selection.artistName}</p>
            <p className="frame-type">{frameLabels[selection.frameType]}</p>
            <p className="description">{selection.description || 'This work is installed from the live ArtChais artwork storage and marketplace records.'}</p>
            <a className="database-link" href={selection.sourceUrl} target="_blank" rel="noreferrer">
              <ArrowUpRight size={17} />
              <span>{formatPrice(selection)}</span>
            </a>
        </section>
      )}

      <section className="bottom-center panel control-dock">
        <div>
          <Move3D size={20} />
          <span>WASD</span>
          <small>{builderActive ? 'Walk' : 'Move'}</small>
        </div>
        <div>
          <MousePointer2 size={20} />
          <span>{builderActive ? 'Left Click' : 'Drag'}</span>
          <small>{builderActive ? 'Rotate' : 'Look'}</small>
        </div>
        <div>
          <Sparkles size={20} />
          <span>{builderActive ? 'Right Click' : 'Click'}</span>
          <small>{builderActive ? 'Place' : 'Select'}</small>
        </div>
      </section>

      {editorEnabled && !editorAuthenticated && (
        <section className="editor-lock panel">
          <div className="panel-title">
            <GalleryHorizontal size={16} />
            <span>World Builder</span>
          </div>
          <form onSubmit={unlockEditor}>
            <input
              type="password"
              value={editorPassword}
              onChange={(event) => setEditorPassword(event.target.value)}
              placeholder="Editor password"
              autoFocus
            />
            <button type="submit">Enter builder</button>
          </form>
          {editorAuthError && <p>{editorAuthError}</p>}
        </section>
      )}

      {builderActive && (
        <section className="layout-editor panel">
          <div className="panel-title">
            <GalleryHorizontal size={16} />
            <span>World Builder</span>
          </div>
          <select
            value={selectedArtworkId}
            onChange={(event) => selectArtworkForPreview(event.target.value)}
          >
            {artworks.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
          {selectedArtwork && (
            <div className="builder-art-preview">
              <img src={selectedArtwork.imageUrl} alt={selectedArtwork.title} />
              <div>
                <strong>{selectedArtwork.title}</strong>
                <span>{frameLabels[selectedArtwork.frameType]}</span>
              </div>
            </div>
          )}
          <div className="editor-buttons">
            <button type="button" onClick={placeSelectedArtwork}>Place frame</button>
            <button type="button" onClick={removeSelectedPlacement} disabled={!selectedPlacement}>Delete selected</button>
          </div>
          <select
            value={selectedPlacement?.id || ''}
            onChange={(event) => setSelectedPlacementId(event.target.value)}
          >
            <option value="">No placed frame selected</option>
            {editorLayout.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
          {selectedPlacement && (
            <>
              <label>
                <span>X</span>
                <input type="range" min="-32" max="32" step="0.1" value={selectedPlacement.x} onChange={(event) => updateSelectedPlacement({ x: Number(event.target.value) })} />
                <em>{selectedPlacement.x.toFixed(1)}</em>
              </label>
              <label>
                <span>Y</span>
                <input type="range" min="0.4" max="5.5" step="0.05" value={selectedPlacement.y} onChange={(event) => updateSelectedPlacement({ y: Number(event.target.value) })} />
                <em>{selectedPlacement.y.toFixed(2)}</em>
              </label>
              <label>
                <span>Z</span>
                <input type="range" min="-24" max="22" step="0.1" value={selectedPlacement.z} onChange={(event) => updateSelectedPlacement({ z: Number(event.target.value) })} />
                <em>{selectedPlacement.z.toFixed(1)}</em>
              </label>
              <label>
                <span>Rot</span>
                <input type="range" min="-3.142" max="3.142" step="0.01" value={selectedPlacement.rotationY} onChange={(event) => updateSelectedPlacement({ rotationY: Number(event.target.value) })} />
                <em>{selectedPlacement.rotationY.toFixed(2)}</em>
              </label>
              <label>
                <span>Scale</span>
                <input type="range" min="0.25" max="2.2" step="0.025" value={selectedPlacement.scale} onChange={(event) => updateSelectedPlacement({ scale: Number(event.target.value) })} />
                <em>{selectedPlacement.scale.toFixed(2)}</em>
              </label>
            </>
          )}
          <button type="button" className="save-layout-button" onClick={saveLayout}>Save layout</button>
          {saveStatus && <p className="save-status">{saveStatus}</p>}
        </section>
      )}
    </main>
  );
}

export default App;
